import React, { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Lock, ArrowRight, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Passwords don\'t match');
      return;
    }
    if (!token) {
      toast.error('Invalid reset link. Request a new one.');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/reset-password`, {
        token,
        new_password: password
      });
      setSuccess(true);
      toast.success('Password reset! You can now log in.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reset failed. The link may be expired. Request a new one.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-vault flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="h-14 w-14 rounded-xl bg-[#9333EA] flex items-center justify-center mx-auto mb-4">
            <Lock className="h-7 w-7 text-white" />
          </div>
          <h1 className="font-heading text-3xl font-black text-white tracking-tight mb-2">
            {success ? 'All Set!' : 'Reset Your Password'}
          </h1>
          <p className="text-[#A19DB0]">
            {success ? 'Your password has been changed.' : 'Enter your new password below.'}
          </p>
        </div>

        {!token && !success && (
          <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl flex items-center gap-3 mb-6">
            <AlertCircle className="h-5 w-5 text-red-400 shrink-0" />
            <div>
              <p className="text-white font-semibold text-sm">Invalid reset link</p>
              <p className="text-xs text-[#A19DB0]">This link is missing or expired. Go back and request a new one.</p>
            </div>
          </div>
        )}

        {success ? (
          <div className="space-y-6">
            <div className="p-6 bg-emerald-500/10 border border-emerald-500/20 rounded-xl text-center">
              <CheckCircle className="h-12 w-12 text-emerald-400 mx-auto mb-3" />
              <p className="text-white font-semibold">Password changed successfully</p>
              <p className="text-sm text-[#A19DB0] mt-1">You can now log in with your new password.</p>
            </div>
            <Link to="/login">
              <Button className="w-full h-12 btn-primary font-semibold" data-testid="go-to-login-button">
                Go to Login <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          </div>
        ) : token && (
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-[#A19DB0]">New Password</Label>
              <Input
                data-testid="new-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-dark h-12"
                placeholder="At least 6 characters"
                required
                minLength={6}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-[#A19DB0]">Confirm Password</Label>
              <Input
                data-testid="confirm-password-input"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="input-dark h-12"
                placeholder="Type it again"
                required
                minLength={6}
              />
            </div>
            <Button
              type="submit"
              data-testid="reset-password-submit-button"
              disabled={loading}
              className="w-full h-12 btn-primary font-semibold"
            >
              {loading ? <><Loader2 className="h-5 w-5 animate-spin mr-2" /> Resetting...</> : 'Reset Password'}
            </Button>
            <p className="text-center text-xs text-[#A19DB0]">
              Remember your password? <Link to="/login" className="text-[#9333EA] hover:text-[#A855F7] font-semibold">Sign in</Link>
            </p>
          </form>
        )}

        {!token && !success && (
          <Link to="/forgot-password">
            <Button className="w-full h-12 btn-primary font-semibold">
              Request New Reset Link
            </Button>
          </Link>
        )}
      </div>
    </div>
  );
}
